import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './UserManager.css';
import AdminViewModal from './AdminViewModal';
import AssignModal from './AssignModal';

const LS_KEY_PREFIX = 'bc:userManager:expanded:';

// ---------- small presentational helpers --------------------------------

const initialsOf = (name = '') =>
  name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';

const avatarColor = (seed = '') => {
  // Deterministic pastel from name
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const palettes = [
    ['#EEEDFE', '#3C3489'], ['#E1F5EE', '#085041'],
    ['#FAEEDA', '#633806'], ['#FAECE7', '#712B13'],
    ['#E6F1FB', '#0C447C'], ['#FCE7F3', '#831843']
  ];
  return palettes[h % palettes.length];
};

const RoleBadge = ({ user }) => {
  if (user.role === 'platform_owner') return <span className="bc-badge bc-badge--owner">Platform owner</span>;
  if (user.role === 'service_partner') return <span className="bc-badge bc-badge--neutral" style={{background:'#ede9fe',color:'#6d28d9',border:'1px solid #c4b5fd'}}>Partner</span>;
  if (user.role === 'introducer') {
    const lvl = user.introducer_level || 'adviser';
    return <span className="bc-badge bc-badge--introducer">Introducer · {lvl}</span>;
  }
  if (user.broker_level === 'manager') return <span className="bc-badge bc-badge--manager">Manager</span>;
  if (user.broker_level === 'owner') return <span className="bc-badge bc-badge--brokerowner">Owner</span>;
  if (user.broker_level === 'adviser') return <span className="bc-badge bc-badge--adviser">Adviser</span>;
  return <span className="bc-badge bc-badge--neutral">{user.role}</span>;
};

const Avatar = ({ name }) => {
  const [bg, fg] = avatarColor(name);
  return <span className="bc-avatar" style={{ background: bg, color: fg }}>{initialsOf(name)}</span>;
};

// ---------- main component ----------------------------------------------

export default function UserManager() {
  const [tree, setTree] = useState({ brokerCompanies: [], unassigned: [] });
  const [allUsersFlat, setAllUsersFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all'); // all | brokers | introducers
  const [editingUser, setEditingUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [creationContext, setCreationContext] = useState(null); // { brokerCompanyId, introducerId }
  const [selectedUserForView, setSelectedUserForView] = useState(null);
  const [mobileActionsFor, setMobileActionsFor] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [searchResultsOpen, setSearchResultsOpen] = useState(false);
  const searchBlurTimer = useRef(null);

  const currentUserId = useMemo(() => {
    try { return JSON.parse(atob(localStorage.getItem('token').split('.')[1])).id; }
    catch { return 'anon'; }
  }, []);
  const lsKey = `${LS_KEY_PREFIX}${currentUserId}`;

  // localStorage-backed expand state (defaults: all expanded on first visit)
  const [expanded, setExpanded] = useState(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      return raw ? new Set(JSON.parse(raw)) : null; // null = "default: all expanded"
    } catch { return null; }
  });

  const persistExpanded = useCallback((next) => {
    setExpanded(next);
    try { localStorage.setItem(lsKey, JSON.stringify(Array.from(next))); } catch {}
  }, [lsKey]);

  const isExpanded = (key) => expanded === null ? true : expanded.has(key);

  const toggle = (key) => {
    const next = new Set(expanded === null
      ? tree.brokerCompanies.flatMap(c => [`co:${c.id}`, ...c.introducers.map(i => `intro:${i.id}`)])
      : expanded);
    if (next.has(key)) next.delete(key); else next.add(key);
    persistExpanded(next);
  };

  // ---- data load ------------------------------------------------------

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/owner/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();

        if (data.tree) setTree(data.tree);
        setAllUsersFlat(data.users || []);
        setError('');
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('Error fetching users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    const interval = setInterval(fetchUsers, 5000);
    return () => clearInterval(interval);
  }, [fetchUsers]);

  // ---- mutations ------------------------------------------------------

  const [formData, setFormData] = useState({
    email: '', name: '', phone: '', role: 'agent', status: 'active',
    broker_company_id: null, introducer_id: null
  });

  const openCreate = (context = {}) => {
    setEditingUser(null);
    setCreationContext(context);
    setFormData({
      email: '', name: '', phone: '',
      role: context.introducerId ? 'introducer' : 'broker',
      status: 'active',
      broker_company_id: context.brokerCompanyId || null,
      introducer_id: context.introducerId || null
    });
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setCreationContext(null);
    setFormData({
      email: user.email,
      name: user.name,
      phone: user.phone || '',
      role: user.role,
      status: user.status,
      broker_company_id: user.broker_company_id,
      introducer_id: user.introducer_id,
      broker_level: user.broker_level || '',
      manager_id: user.manager_id || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingUser ? `/api/users/${editingUser.id}` : '/api/users';
      // routes/users.js only registers PATCH /:userId for edits (POST / for create) — this
      // used to send PUT, which matches no route (404), so every edit-save silently failed.
      const method = editingUser ? 'PATCH' : 'POST';
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSuccess(editingUser ? 'User updated' : 'User created');
        setShowForm(false);
        setEditingUser(null);
        fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Surface the server's actual reason instead of a generic message — this exact bug
        // (wrong HTTP method → 404) was much harder to diagnose while this swallowed the detail.
        let detail = '';
        try { detail = (await response.json()).error || ''; } catch {}
        setError(detail ? `Failed to save user: ${detail}` : `Failed to save user (${response.status})`);
      }
    } catch (err) {
      setError('Error: ' + err.message);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.name}? This cannot be undone.`)) return;
    try {
      const response = await fetch(`/api/users/${user.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setSuccess('User deleted');
        fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete user');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setMobileActionsFor(null);
    }
  };

  // ---- search/filter --------------------------------------------------

  const matchesSearch = (user) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (user.name || '').toLowerCase().includes(s) ||
           (user.email || '').toLowerCase().includes(s);
  };

  const filteredTree = useMemo(() => {
    if (!search && filterType === 'all') return tree;

    const filterUserList = (list) => list
      .map(u => ({ ...u, directReports: (u.directReports || []).filter(matchesSearch) }))
      .filter(u => matchesSearch(u) || u.directReports.length > 0);

    const brokerCompanies = tree.brokerCompanies
      .map(c => {
        const users = filterType === 'introducers' ? [] : filterUserList(c.users);
        const introducers = filterType === 'brokers' ? [] : c.introducers
          .map(i => ({ ...i, users: filterUserList(i.users) }))
          .filter(i => (search ? i.name.toLowerCase().includes(search.toLowerCase()) : true) || i.users.length > 0);
        return { ...c, users, introducers };
      })
      .filter(c => {
        if (!search) return c.users.length > 0 || c.introducers.length > 0;
        return c.name.toLowerCase().includes(search.toLowerCase())
          || c.users.length > 0
          || c.introducers.length > 0;
      });

    return { ...tree, brokerCompanies };
  }, [tree, search, filterType]);

  // ---- quick-jump search dropdown --------------------------------------
  // Filters the user list already sitting in state (allUsersFlat comes from the same
  // /api/owner/users call the tree uses, refreshed on a 5s interval regardless of typing) —
  // this does NOT hit the API per keystroke, and there is no separate "fetch all users"
  // request triggered by the search box.

  const companyNameById = useMemo(() => {
    const map = new Map();
    tree.brokerCompanies.forEach(c => map.set(c.id, c.name));
    return map;
  }, [tree.brokerCompanies]);

  const emailSearchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allUsersFlat
      .map(u => {
        const email = (u.email || '').toLowerCase();
        const name = (u.name || '').toLowerCase();
        let rank = -1;
        if (email.startsWith(q)) rank = 0;
        else if (name.startsWith(q)) rank = 1;
        else if (email.includes(q)) rank = 2;
        else if (name.includes(q)) rank = 3;
        return rank === -1 ? null : { user: u, rank };
      })
      .filter(Boolean)
      .sort((a, b) => a.rank - b.rank || (a.user.name || '').localeCompare(b.user.name || ''))
      .slice(0, 8)
      .map(r => r.user);
  }, [allUsersFlat, search]);

  useEffect(() => () => { if (searchBlurTimer.current) clearTimeout(searchBlurTimer.current); }, []);

  const handleSearchChange = (e) => { setSearch(e.target.value); setSearchResultsOpen(true); };
  const handleSearchFocus = () => { if (search.trim()) setSearchResultsOpen(true); };
  const handleSearchBlur = () => {
    // Small delay so a click on a result (see onMouseDown below) registers before we unmount it
    searchBlurTimer.current = setTimeout(() => setSearchResultsOpen(false), 150);
  };
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') { setSearchResultsOpen(false); e.currentTarget.blur(); }
  };
  const pickSearchResult = (user) => {
    if (searchBlurTimer.current) clearTimeout(searchBlurTimer.current);
    setSearchResultsOpen(false);
    setSearch('');
    openEdit(user);
  };

  // ---- row renderer ---------------------------------------------------

  const UserRow = ({ user, level = 0, withConnector = false, contextLabel = null }) => (
    <div className={`bc-row bc-row--level-${level}`}>
      {withConnector && <span className="bc-connector" aria-hidden="true" />}
      <Avatar name={user.name} />
      <div className="bc-row__main">
        <div className="bc-row__name">
          {user.name}
          <span className="bc-row__email">{user.email}</span>
        </div>
        <div className="bc-row__meta">
          {contextLabel ||
            (user.role === 'service_partner' ? 'Service partner'
              : user.role === 'platform_owner' ? 'Platform owner'
              : user.introducer_id
                ? (user.introducer_level === 'manager' && user.directReports?.length
                    ? `Manager · ${user.directReports.length} direct report${user.directReports.length === 1 ? '' : 's'}`
                    : user.introducer_level === 'company' ? 'Company introducer'
                    : user.introducer_level === 'solo' ? 'Solo introducer'
                    : user.introducer_level === 'manager' ? 'Manager'
                    : user.introducer_level === 'adviser' ? 'Adviser'
                    : 'Introducer')
                : (user.broker_level === 'manager' && user.directReports?.length
                    ? `Manager · ${user.directReports.length} direct report${user.directReports.length === 1 ? '' : 's'}`
                    : user.broker_level === 'owner' ? 'Company owner'
                    : user.broker_level === 'manager' ? 'Manager'
                    : user.broker_level === 'adviser' ? 'Adviser'
                    : user.role))}
        </div>
      </div>
      <RoleBadge user={user} />
      <div className="bc-row__actions bc-row__actions--desktop">
        <button className="bc-iconbtn bc-iconbtn--assign" onClick={() => setAssignTarget(user)} aria-label="Assign" title={user.broker_company_id || user.introducer_id ? 'Move' : 'Assign'}>➡️</button>
        <button className="bc-iconbtn" onClick={() => setSelectedUserForView(user)} aria-label="View" title="View">👁</button>
        <button className="bc-iconbtn" onClick={() => openEdit(user)} aria-label="Edit" title="Edit">✏️</button>
        <button className="bc-iconbtn bc-iconbtn--danger" onClick={() => handleDelete(user)} aria-label="Delete" title="Delete">🗑</button>
      </div>
      <button
        className="bc-iconbtn bc-row__actions--mobile"
        onClick={() => setMobileActionsFor(user)}
        aria-label="More actions"
      >⋯</button>
    </div>
  );

  // ---- render ---------------------------------------------------------

  if (loading) return <div className="bc-um"><div className="bc-um__loading">Loading users…</div></div>;

  return (
    <div className="bc-um">
      <div className="bc-um__header">
        <h2>User Manager</h2>
        <div className="bc-um__toolbar">
          <div className="bc-um__search-wrap">
            <input
              type="text"
              className="bc-input"
              placeholder="Search by name or email…"
              value={search}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              onKeyDown={handleSearchKeyDown}
              autoComplete="off"
            />
            {searchResultsOpen && search.trim() && (
              <div className="bc-um__search-results" role="listbox">
                {emailSearchResults.length === 0 ? (
                  <div className="bc-um__search-empty">No matching users</div>
                ) : (
                  emailSearchResults.map(u => (
                    <button
                      type="button"
                      key={u.id}
                      className="bc-um__search-result"
                      // onMouseDown (not onClick) fires before the input's onBlur, and
                      // preventDefault stops that blur from closing this list first
                      onMouseDown={(e) => { e.preventDefault(); pickSearchResult(u); }}
                    >
                      <Avatar name={u.name} />
                      <span className="bc-um__search-result-text">
                        <span className="bc-um__search-result-name">{u.name}</span>
                        <span className="bc-um__search-result-email">{u.email}</span>
                      </span>
                      <span className="bc-um__search-result-meta">
                        {companyNameById.get(u.broker_company_id) || (u.introducer_id ? 'Introducer' : '')}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <select className="bc-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All types</option>
            <option value="brokers">Brokers only</option>
            <option value="introducers">Introducers only</option>
          </select>
          <button className="bc-btn bc-btn--primary" onClick={() => openCreate({})}>+ Add user</button>
        </div>
      </div>

      {error && <div className="bc-alert bc-alert--error">{error}</div>}
      {success && <div className="bc-alert bc-alert--success">{success}</div>}

      <div className="bc-section-label">Broker companies</div>

      {filteredTree.brokerCompanies.length === 0 && (
        <div className="bc-empty">No broker companies match.</div>
      )}

      {filteredTree.brokerCompanies.map(co => {
        const coKey = `co:${co.id}`;
        const openCo = isExpanded(coKey);
        const userCount = co.users.length + co.users.reduce((n, u) => n + (u.directReports?.length || 0), 0);
        const introCount = co.introducers.length;

        return (
          <div className="bc-card" key={co.id}>
            <button className="bc-card__header" onClick={() => toggle(coKey)} aria-expanded={openCo}>
              <span className={`bc-chev ${openCo ? 'bc-chev--open' : ''}`}>▸</span>
              <span className="bc-card__icon">🏢</span>
              <div className="bc-card__title">
                <div className="bc-card__name">{co.name}</div>
                <div className="bc-card__sub">
                  {userCount} broker{userCount === 1 ? '' : 's'} · {introCount} introducer{introCount === 1 ? '' : 's'}
                </div>
              </div>
              <span className="bc-badge bc-badge--broker">Broker</span>
              <span
                className="bc-btn bc-btn--ghost bc-card__add"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); openCreate({ brokerCompanyId: co.id }); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openCreate({ brokerCompanyId: co.id }); } }}
              >+ Add</span>
            </button>

            {openCo && (
              <div className="bc-card__body">
                {co.users.length > 0 && (
                  <>
                    <div className="bc-subhead">Brokers</div>
                    {co.users.map(u => (
                      <React.Fragment key={u.id}>
                        <UserRow user={u} level={1} />
                        {u.directReports?.map(r => (
                          <UserRow key={r.id} user={r} level={2} withConnector contextLabel={`Adviser · reports to ${u.name.split(' ')[0]}`} />
                        ))}
                      </React.Fragment>
                    ))}
                  </>
                )}

                {co.introducers.length > 0 && (
                  <>
                    <div className="bc-subhead bc-subhead--with-action">
                      <span>Introducers</span>
                      <button
                        className="bc-btn bc-btn--ghost-sm"
                        onClick={() => openCreate({ brokerCompanyId: co.id, asIntroducer: true })}
                      >+ Add</button>
                    </div>
                    {co.introducers.map(intro => {
                      const introKey = `intro:${intro.id}`;
                      const openIntro = isExpanded(introKey);
                      const assignedTo = intro.assignedBrokers.filter(a => a.brokerName).map(a => a.brokerName);
                      return (
                        <div className="bc-subcard" key={intro.id}>
                          <button className="bc-subcard__header" onClick={() => toggle(introKey)} aria-expanded={openIntro}>
                            <span className={`bc-chev bc-chev--sm ${openIntro ? 'bc-chev--open' : ''}`}>▸</span>
                            <span className="bc-card__icon bc-card__icon--sm">👥</span>
                            <div className="bc-subcard__title">
                              <div className="bc-subcard__name">{intro.name}</div>
                              <div className="bc-subcard__sub">
                                {intro.users.length} user{intro.users.length === 1 ? '' : 's'}
                                {assignedTo.length > 0 && ` · assigned to ${assignedTo.join(', ')}`}
                              </div>
                            </div>
                            <span
                              className={`bc-badge ${intro.isHome ? 'bc-badge--home' : 'bc-badge--assigned'}`}
                              title={intro.isHome
                                ? `${co.name}'s own introducer`
                                : `Belongs to another company, shared with ${co.name}`}
                            >
                              {intro.isHome
                                ? 'Own introducer'
                                : `Shared from ${tree.brokerCompanies.find(c => c.id === intro.broker_company_id)?.name || 'another company'}`}
                            </span>
                          </button>
                          {openIntro && intro.users.map(u => (
                            <React.Fragment key={u.id}>
                              <UserRow user={u} level={2} />
                              {u.directReports?.map(r => (
                                <UserRow key={r.id} user={r} level={3} withConnector contextLabel={`Reports to ${u.name.split(' ')[0]}`} />
                              ))}
                            </React.Fragment>
                          ))}
                        </div>
                      );
                    })}
                  </>
                )}

                {co.users.length === 0 && co.introducers.length === 0 && (
                  <div className="bc-empty bc-empty--inset">No users in this company yet.</div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {tree.unassigned && tree.unassigned.length > 0 && (
        <>
          <div className="bc-section-label">Unassigned</div>
          <div className="bc-card">
            <div className="bc-card__body">
              {tree.unassigned.map(u => <UserRow key={u.id} user={u} level={1} />)}
            </div>
          </div>
        </>
      )}

      {/* ---- Create/Edit modal ---- */}
      {showForm && (
        <div className="bc-modal" onClick={() => setShowForm(false)}>
          <div className="bc-modal__panel" onClick={(e) => e.stopPropagation()}>
            <div className="bc-modal__panel-head">
              <h3>{editingUser ? 'Edit user' : 'Add user'}</h3>
              <button type="button" className="bc-modal__close" onClick={() => setShowForm(false)} aria-label="Close">✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <label>Name<input className="bc-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></label>
              <label>Email<input className="bc-input" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required /></label>
              <label>Phone<input className="bc-input" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></label>
              <label>Role
                <select className="bc-select" value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })}>
                  <option value="broker">Broker</option>
                  <option value="introducer">Introducer</option>
                  <option value="platform_owner">Platform Owner</option>
                </select>
              </label>
              {(formData.role === 'broker' || formData.role === 'agent') && (<>
                <label>Broker Level
                  <select className="bc-select" value={formData.broker_level || ''} onChange={(e) => setFormData({ ...formData, broker_level: e.target.value })}>
                    <option value="">— Not set —</option>
                    <option value="owner">Owner</option>
                    <option value="manager">Manager</option>
                    <option value="adviser">Adviser</option>
                  </select>
                </label>
                <label>Manager ID <span style={{fontSize:11,color:'#a0aec0',fontWeight:400}}>(optional — paste user ID)</span>
                  <input className="bc-input" value={formData.manager_id || ''} onChange={(e) => setFormData({ ...formData, manager_id: e.target.value || null })} placeholder="UUID of manager" />
                </label>
              </>)}
              <label>Status
                <select className="bc-select" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </label>
              <div className="bc-modal__actions">
                <button type="button" className="bc-btn" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="bc-btn bc-btn--primary">{editingUser ? 'Save' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---- Mobile action sheet ---- */}
      {mobileActionsFor && (
        <div className="bc-sheet" onClick={() => setMobileActionsFor(null)}>
          <div className="bc-sheet__panel" onClick={(e) => e.stopPropagation()}>
            <div className="bc-sheet__title">{mobileActionsFor.name}</div>
            <button className="bc-sheet__action" onClick={() => { setAssignTarget(mobileActionsFor); setMobileActionsFor(null); }}>{mobileActionsFor.broker_company_id || mobileActionsFor.introducer_id ? 'Move / Assign' : 'Assign'}</button>
            <button className="bc-sheet__action" onClick={() => { setSelectedUserForView(mobileActionsFor); setMobileActionsFor(null); }}>View details</button>
            <button className="bc-sheet__action" onClick={() => { openEdit(mobileActionsFor); setMobileActionsFor(null); }}>Edit</button>
            <button className="bc-sheet__action bc-sheet__action--danger" onClick={() => handleDelete(mobileActionsFor)}>Delete</button>
            <button className="bc-sheet__action" onClick={() => setMobileActionsFor(null)}>Cancel</button>
          </div>
        </div>
      )}

      {assignTarget && (
        <AssignModal
          user={assignTarget}
          tree={tree}
          onClose={() => setAssignTarget(null)}
          onAssigned={fetchUsers}
        />
      )}

      {selectedUserForView && (
        <AdminViewModal user={selectedUserForView} onClose={() => setSelectedUserForView(null)} />
      )}
    </div>
  );
}
